import { c as _c } from "react/compiler-runtime";
import type { OnboardingRequirementCollectDocument, OnboardingSessionConfig } from '@onefootprint/request-types';
import { useEffect } from 'react';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger, trackAction } from '@/idv2/utils/logger';
import DeviceNotSupportedError from './components/device-not-supported-error';
import IdDoc from './id-doc';
import NonIdDoc from './non-id-doc';
import Selfie from './selfie';
import type { IdDocOutcome } from './types';
import { isMobileKind } from './utils/capture';

// trackAction only reaches RUM for allow-listed tenants; the log index needs these events for
// every session.
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'collect-document'
});
type CollectDocumentProps = {
  authToken: string;
  device: DeviceInfo;
  requirement: OnboardingRequirementCollectDocument;
  onboardingConfig: OnboardingSessionConfig;
  sandboxOutcome?: IdDocOutcome;
  onDone: () => void;
};
const CollectDocument = t0 => {
  const $ = _c(26);
  const {
    authToken,
    device,
    requirement,
    onboardingConfig,
    sandboxOutcome,
    onDone
  } = t0;
  const requireMobileCapture = requirement.uploadSettings === "require_mobile_capture";
  const showDeviceError = requireMobileCapture && !isMobileKind(device.type);
  let t1;
  if ($[0] !== onDone || $[1] !== requirement.config.kind) {
    t1 = () => {
      trackAction("doc:completed", {
        kind: requirement.config.kind
      });
      logInfo("Document collection completed", {
        kind: requirement.config.kind
      });
      onDone();
    };
    $[0] = onDone;
    $[1] = requirement.config.kind;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleFlowCompletion = t1;
  let t2;
  if ($[3] !== requirement.config.kind) {
    t2 = () => {
      trackAction("doc:started", {
        kind: requirement.config.kind
      });
      logInfo("Document collection started", {
        kind: requirement.config.kind
      });
    };
    $[3] = requirement.config.kind;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  if (showDeviceError) {
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <DeviceNotSupportedError />;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  if (isIdDocRequirement(requirement)) {
    let t4;
    if ($[7] !== authToken || $[8] !== device || $[9] !== handleFlowCompletion || $[10] !== onboardingConfig || $[11] !== requirement || $[12] !== sandboxOutcome) {
      t4 = <IdDoc key={requirement.documentRequestId} authToken={authToken} device={device} requirement={requirement} sandboxOutcome={sandboxOutcome} onboardingConfig={onboardingConfig} onDone={handleFlowCompletion} />;
      $[7] = authToken;
      $[8] = device;
      $[9] = handleFlowCompletion;
      $[10] = onboardingConfig;
      $[11] = requirement;
      $[12] = sandboxOutcome;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  if (requirement.config.kind === "selfie") {
    let t4;
    if ($[14] !== authToken || $[15] !== device || $[16] !== handleFlowCompletion || $[17] !== onboardingConfig || $[18] !== requirement) {
      t4 = <Selfie authToken={authToken} device={device} requirement={requirement} onboardingConfig={onboardingConfig} onDone={handleFlowCompletion} />;
      $[14] = authToken;
      $[15] = device;
      $[16] = handleFlowCompletion;
      $[17] = onboardingConfig;
      $[18] = requirement;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    return t4;
  }
  let t4;
  if ($[20] !== authToken || $[21] !== device || $[22] !== handleFlowCompletion || $[23] !== onboardingConfig || $[24] !== requirement) {
    t4 = <NonIdDoc authToken={authToken} device={device} requirement={requirement} onboardingConfig={onboardingConfig} onDone={handleFlowCompletion} />;
    $[20] = authToken;
    $[21] = device;
    $[22] = handleFlowCompletion;
    $[23] = onboardingConfig;
    $[24] = requirement;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  return t4;
};
const isIdDocRequirement = (requirement: OnboardingRequirementCollectDocument): requirement is OnboardingRequirementCollectDocument & {
  config: {
    kind: 'identity';
    shouldCollectConsent: boolean;
    shouldCollectSelfie: boolean;
  };
} => requirement.config.kind === 'identity';
export default CollectDocument;